import React from 'react';
import { useQuery, useQueries, QueryClient } from '@tanstack/react-query';
import { useAuthStore, useDashboardStore } from '@/stores';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

interface BatchedQuery {
  key: string[];
  queryFn: () => Promise<any>;
  staleTime?: number;
  enabled?: boolean;
}

interface UnifiedDashboardResult {
  recentRuns: any[];
  recentFitness: any[];
  bodyComposition: any;
  progressPhotos: any[];
  personalRecords: any[];
  stravaConnection: any;
  isLoading: boolean;
  errors: Record<string, any>;
}

export const useUnifiedDashboardQuery = (): UnifiedDashboardResult => {
  const { user } = useAuthStore();
  const { setData, isStale } = useDashboardStore();
  const componentLogger = logger.createComponentLogger('UnifiedDashboardQuery');

  // Define all dashboard queries in batch
  const batchedQueries: BatchedQuery[] = [
    {
      key: ['running-performance', user?.id],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('running_performance')
          .select(`
            id, recorded_date, distance_meters, time_seconds, pace_per_km_seconds,
            heart_rate_avg, heart_rate_max, calories_burned, data_source,
            strava_activity_id, trimp_score, hr_efficiency_score
          `)
          .eq('user_id', user!.id)
          .order('recorded_date', { ascending: false })
          .limit(10);
        if (error) throw error;
        return data;
      },
      staleTime: 1000 * 60 * 10, // 10 minutes
    },
    {
      key: ['general-fitness', user?.id],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('general_fitness')
          .select(`
            id, recorded_date, activity_type, duration_seconds, calories_burned,
            intensity_level, data_source, strava_activity_id
          `)
          .eq('user_id', user!.id)
          .order('recorded_date', { ascending: false })
          .limit(10);
        if (error) throw error;
        return data;
      },
      staleTime: 1000 * 60 * 10,
    },
    {
      key: ['body-composition', user?.id],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('body_composition')
          .select('*')
          .eq('user_id', user!.id)
          .order('recorded_date', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        return data;
      },
      staleTime: 1000 * 60 * 30, // 30 minutes
    },
    {
      key: ['progress-photos', user?.id],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('progress_photos')
          .select('id, upload_date, photo_type, is_favorite')
          .eq('user_id', user!.id)
          .order('upload_date', { ascending: false })
          .limit(5);
        if (error) throw error;
        return data;
      },
      staleTime: 1000 * 60 * 15, // 15 minutes
    },
    {
      key: ['personal-records', user?.id],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('running_personal_records')
          .select('*')
          .eq('user_id', user!.id)
          .order('achieved_date', { ascending: false })
          .limit(5);
        if (error) throw error;
        return data;
      },
      staleTime: 1000 * 60 * 60, // 1 hour
    },
    {
      key: ['strava-connection', user?.id],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('strava_credentials')
          .select('is_active, last_successful_sync, athlete_firstname')
          .eq('user_id', user!.id)
          .maybeSingle();
        if (error && error.code !== 'PGRST116') throw error;
        return data;
      },
      staleTime: 1000 * 60 * 60, // 1 hour
    },
  ];

  // Execute all queries in parallel with automatic deduplication
  const results = useQueries({
    queries: batchedQueries.map((query) => ({
      queryKey: ['unified-dashboard', ...query.key],
      queryFn: async () => {
        const startTime = performance.now();
        try {
          const result = await query.queryFn();
          const duration = performance.now() - startTime;
          componentLogger.debug('Query completed', {
            queryKey: query.key,
            duration: `${duration.toFixed(2)}ms`
          });
          return result;
        } catch (error) {
          componentLogger.error('Query failed', { queryKey: query.key, error });
          throw error;
        }
      },
      enabled: !!user,
      staleTime: query.staleTime || 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
    })),
  });

  // Update store cache when fresh data arrives
  React.useEffect(() => {
    const allQueriesSuccessful = results.every(result => result.isSuccess);
    if (allQueriesSuccessful && !isStale()) {
      const [runs, fitness, bodyComp, photos, records, strava] = results.map(r => r.data);
      setData({
        recentRuns: runs || [],
        recentFitness: fitness || [],
        bodyComposition: bodyComp,
        photos: photos || [],
        lastUpdated: Date.now()
      });
    }
  }, [results, setData, isStale]);

  return {
    recentRuns: results[0]?.data || [],
    recentFitness: results[1]?.data || [],
    bodyComposition: results[2]?.data || null,
    progressPhotos: results[3]?.data || [],
    personalRecords: results[4]?.data || [],
    stravaConnection: results[5]?.data || null,
    isLoading: results.some(result => result.isLoading),
    errors: results.reduce((acc, result, index) => {
      if (result.error) {
        acc[batchedQueries[index].key.join('-')] = result.error;
      }
      return acc;
    }, {} as Record<string, any>),
  };
};